import {
  ContractorNotApprovedStatusReason,
  ContractorPausedStatusReason,
  ProjectDigestFrequency,
} from "@prisma/client";
import { Media } from "@yardzen/media";
import { Expose, plainToInstance, Type } from "class-transformer";
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsEmail,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  validateSync,
} from "class-validator";
import { Address } from "../address.entity.ts/address.entity";
import { ContractorAttribute } from "./contractor-attribute.enum";
import { ContractorContact } from "./contractor-contact.entity";
import { ContractorDocument } from "./contractor-document.entity";
import { ContractorReview } from "./contractor-review.entity";

export class ContractorProfile {
  public static from(contractorProfile: ContractorProfile) {
    const instance = plainToInstance(ContractorProfile, contractorProfile, {
      excludeExtraneousValues: true,
      enableCircularCheck: true,
    });

    instance.promotionalMessageStartDate = instance.promotionalMessageStartDate
      ? new Date(instance.promotionalMessageStartDate)
      : undefined;
    instance.promotionalMessageEndDate = instance.promotionalMessageEndDate
      ? new Date(instance.promotionalMessageEndDate)
      : undefined;

    const errors = validateSync(instance);
    if (errors.length > 0) {
      throw new Error(
        `Validation failed: ${errors.map((error) => Object.values(error.constraints || {})).join(", ")}`,
      );
    }

    return instance;
  }

  @Expose()
  @IsString()
  @IsNotEmpty()
  contractorId!: string;

  @Expose()
  @IsString()
  @IsOptional()
  userId?: string;

  @Expose()
  @IsString()
  @IsNotEmpty()
  @IsEmail()
  @IsOptional()
  email?: string;

  @Expose()
  @IsArray()
  @IsOptional()
  media?: Media[];

  @Expose()
  @IsString()
  @IsNotEmpty()
  @IsOptional()
  businessName?: string;

  @Expose()
  @IsString()
  @IsOptional()
  headshotMediaUrl?: string;

  @Expose()
  @IsString()
  @IsOptional()
  thumbnailMediaUrl?: string;

  @Expose()
  @IsArray()
  @IsOptional()
  galleryImages?: { id: string; uri: string }[];

  @Expose()
  @IsArray()
  @IsOptional()
  videoMedia?: { id: string; uri: string }[];

  @Expose()
  @IsDate()
  @IsNotEmpty()
  @Type(() => Date)
  createdTs!: Date;

  @Expose()
  @IsDate()
  @IsNotEmpty()
  @Type(() => Date)
  updatedTs!: Date;

  @Expose()
  @IsArray()
  @IsOptional()
  attributes?: ContractorAttribute[];

  @Expose()
  @IsString()
  @IsOptional()
  aboutSummary?: string;

  @Expose()
  @IsString()
  @IsOptional()
  googleUrl?: string;

  @Expose()
  @IsString()
  @IsOptional()
  yelpUrl?: string;

  @Expose()
  @IsString()
  @IsOptional()
  whyYardzenLovesSummary?: string;

  @Expose()
  @IsInt()
  @IsOptional()
  yearsInBusiness?: number;

  @Expose()
  @IsString()
  @IsOptional()
  licenseNumber?: string;

  @Expose()
  @IsDate()
  @IsOptional()
  licenseExpirationDate?: Date;

  @Expose()
  @IsString()
  @IsOptional()
  promotionalMessage?: string;

  @Expose()
  @IsDate()
  @IsOptional()
  promotionalMessageStartDate?: Date;

  @Expose()
  @IsDate()
  @IsOptional()
  promotionalMessageEndDate?: Date;

  @Expose()
  @IsArray()
  @IsOptional()
  reviews?: ContractorReview[];

  @Expose()
  @IsNumber()
  @IsOptional()
  rating?: number;

  @Expose()
  @IsBoolean()
  active!: boolean;

  @Expose()
  @IsBoolean()
  paused!: boolean;

  @Expose()
  @IsDate()
  @Type(() => Date)
  @IsOptional()
  pausedTs?: Date;

  @Expose()
  @IsDate()
  @Type(() => Date)
  @IsOptional()
  unpausedTs?: Date;

  @Expose()
  @IsBoolean()
  approved!: boolean;

  @Expose()
  @IsDate()
  @Type(() => Date)
  @IsOptional()
  approvedTs?: Date;

  @Expose()
  @IsDate()
  @Type(() => Date)
  @IsOptional()
  unapprovedTs?: Date;

  @Expose()
  @IsBoolean()
  @IsOptional()
  isTest?: boolean;

  @Expose()
  @IsString()
  @IsOptional()
  stripeConnectedAccountId?: string;

  @Expose()
  @IsInt()
  @IsOptional()
  minSuccessBudget?: number;

  @Expose()
  @IsInt()
  @IsOptional()
  maxSuccessBudget?: number;

  @Expose()
  @IsInt()
  @IsOptional()
  budgetRangeLowerBound?: number;

  @Expose()
  @IsInt()
  @IsOptional()
  budgetRangeUpperBound?: number;

  @Expose()
  @IsEnum(ProjectDigestFrequency)
  @IsOptional()
  projectDigestFrequency?: ProjectDigestFrequency;

  @Expose()
  @IsString()
  @IsOptional()
  addressId?: string;

  @Expose()
  @IsOptional()
  address?: Address;

  @Expose()
  @IsEnum(ContractorPausedStatusReason)
  @IsOptional()
  pausedStatusReason?: ContractorPausedStatusReason;

  @Expose()
  @IsEnum(ContractorNotApprovedStatusReason)
  @IsOptional()
  notApprovedStatusReason?: ContractorNotApprovedStatusReason;

  @Expose()
  @IsBoolean()
  @IsOptional()
  acceptingWork?: boolean;

  @Expose()
  @IsString()
  @IsOptional()
  website?: string;

  @Expose()
  @IsBoolean()
  @IsOptional()
  preferred?: boolean;

  @Expose()
  @IsString()
  @IsOptional()
  notes?: string;

  @Expose()
  @IsString()
  @IsOptional()
  currentBuildRegionId?: string;

  @Expose()
  @IsString()
  @IsOptional()
  initialBuildRegionId?: string;

  @Expose()
  @IsString()
  @IsOptional()
  createdBy?: string;

  @Expose()
  @IsString()
  @IsOptional()
  updatedBy?: string;

  @Expose()
  @IsString()
  @IsOptional()
  phoneNumber?: string;

  @Expose()
  @IsString()
  @IsOptional()
  city?: string;

  @Expose()
  @IsString()
  @IsOptional()
  state?: string;

  @Expose()
  @IsString()
  @IsOptional()
  firstName?: string;

  @Expose()
  @IsString()
  @IsOptional()
  lastName?: string;

  @Expose()
  @IsNumber()
  @IsOptional()
  latitude?: number;

  @Expose()
  @IsNumber()
  @IsOptional()
  longitude?: number;

  @Expose()
  @IsArray()
  @IsOptional()
  abilities?: string[];

  @IsArray()
  @IsOptional()
  documents?: ContractorDocument[];

  @Expose()
  @Type(() => ContractorContact)
  @IsArray()
  @IsOptional()
  contacts?: ContractorContact[];
}
